import {
  FiClock,
  FiFileText,
  FiLayers,
  FiCalendar,
  FiCheck,
  FiHeart,
} from "react-icons/fi";
import { FaTelegramPlane } from "react-icons/fa";
import { AnonymizedApplicant } from "@/lib/types";
import { formatDate, getSchemeBadgeColor } from "@/lib/utils";

interface Props {
  applicant: AnonymizedApplicant;
  onRequestContact: () => void;
  isRequesting: boolean;
  isRequested: boolean;
}

export default function ApplicantCard({
  applicant,
  onRequestContact,
  isRequesting,
  isRequested,
}: Props) {
  return (
    <div className="bg-white rounded-lg shadow p-4">
      {/* Scheme Badge */}
      <span
        className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold border ${getSchemeBadgeColor(applicant.scheme)}`}
      >
        {applicant.scheme}
      </span>

      {/* Experience */}
      <div className="mt-3 flex items-center gap-2 text-gray-700">
        <FiClock className="text-gray-500" size={16} />
        <span>{applicant.yearsOfExperience} years experience</span>
      </div>

      {/* Subjects */}
      <div className="mt-2 flex items-start gap-2">
        <div className="mt-0.5">
          <FiFileText className="text-gray-500" size={16} />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {applicant.subjects.map((subject) => (
            <span
              key={subject}
              className="px-2 py-0.5 bg-gray-100 rounded text-sm text-gray-700"
            >
              {subject}
            </span>
          ))}
        </div>
      </div>

      {/* Preferred Levels */}
      <div className="mt-2 flex items-start gap-2">
        <div className="mt-0.5">
          <FiHeart className="text-gray-500" size={16} />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {applicant.preferredLevels.map((level) => (
            <span
              key={level}
              className="px-2 py-0.5 bg-blue-100 rounded text-sm text-blue-700"
            >
              {level}
            </span>
          ))}
        </div>
      </div>

      {/* Levels */}
      <div className="mt-2 flex items-start gap-2">
        <div className="mt-0.5">
          <FiLayers className="text-gray-500" size={16} />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {applicant.levels.map((level) => (
            <span
              key={level}
              className="px-2 py-0.5 bg-gray-100 rounded text-sm text-gray-700"
            >
              {level}
            </span>
          ))}
        </div>
      </div>

      {/* Availability */}
      <div className="mt-2 flex items-center gap-2 text-gray-700">
        <FiCalendar className="text-gray-500" size={16} />
        <span>
          {formatDate(applicant.startDate)} to {formatDate(applicant.endDate)}
        </span>
      </div>

      {/* CTA Button */}
      <button
        onClick={onRequestContact}
        disabled={isRequesting || isRequested}
        className={`mt-4 w-full py-3 px-4 rounded-lg flex items-center justify-center gap-2 font-medium transition-colors ${
          isRequested
            ? "bg-green-600 text-white cursor-default"
            : "bg-primary-700 text-white hover:bg-primary-600 disabled:opacity-70"
        }`}
      >
        {isRequested ? (
          <>
            <FiCheck size={18} />
            <span>Sent to Telegram</span>
          </>
        ) : (
          <>
            <FaTelegramPlane size={18} />
            <span>
              {isRequesting ? "Sending..." : "Get contact details via Telegram"}
            </span>
          </>
        )}
      </button>
    </div>
  );
}
